<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Overtime</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoadingRows" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pt-2">
      <div class="grid grid-cols-1 md:grid-cols-4 items-end gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">OT Name</label>
          <input
            v-model.trim="form.otName"
            type="text"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="OT Name"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">OT Rate</label>
          <input
            v-model.trim="form.otRate"
            type="text"
            inputmode="decimal"
            class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Rate"
            required
          >
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Start Time</label>
          <div class="relative rounded-md isolate">
            <input
              v-model="form.startTime"
              type="text"
              maxlength="8"
              inputmode="numeric"
              autocomplete="off"
              placeholder="00:00:00"
              title="24-hour time hh:mm:ss"
              class="w-full px-2.5 py-1.5 pr-10 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm tabular-nums"
              required
              @blur="normalizeTimeFieldOnBlur('startTime')"
            >
            <span class="pointer-events-none absolute right-2.5 top-1/2 z-0 -translate-y-1/2 text-slate-400" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="w-5 h-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
              </svg>
            </span>
            <input
              type="time"
              step="1"
              tabindex="-1"
              class="absolute right-0 top-px z-[1] h-[calc(100%-2px)] w-11 cursor-pointer opacity-0"
              aria-label="Open time picker"
              title="Pick time"
              :value="form.startTime"
              @input="onNativeTimePick('startTime', $event)"
            >
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">End Time</label>
          <div class="relative rounded-md isolate">
            <input
              v-model="form.endTime"
              type="text"
              maxlength="8"
              inputmode="numeric"
              autocomplete="off"
              placeholder="00:00:00"
              title="24-hour time hh:mm:ss"
              class="w-full px-2.5 py-1.5 pr-10 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm tabular-nums"
              required
              @blur="normalizeTimeFieldOnBlur('endTime')"
            >
            <span class="pointer-events-none absolute right-2.5 top-1/2 z-0 -translate-y-1/2 text-slate-400" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="w-5 h-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
              </svg>
            </span>
            <input
              type="time"
              step="1"
              tabindex="-1"
              class="absolute right-0 top-px z-[1] h-[calc(100%-2px)] w-11 cursor-pointer opacity-0"
              aria-label="Open time picker"
              title="Pick time"
              :value="form.endTime"
              @input="onNativeTimePick('endTime', $event)"
            >
          </div>
        </div>
      </div>

      <div class="flex justify-center pt-4 pb-1">
        <button
          v-if="editingId"
          type="submit"
          :disabled="!canEditOvertime || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Update Changes</span>
        </button>
        <button
          v-else
          type="submit"
          :disabled="!canAddOvertime || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
      </div>
    </form>

    <div v-if="!isLoadingRows" class="px-6 pt-4 pb-6">
      <div class="w-full max-lg:overflow-x-auto">
        <table class="w-full max-lg:min-w-[760px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <caption class="caption-top text-left pb-2 text-[0.78rem] font-semibold text-[#1e293b]">
            Overtime definitions
          </caption>
          <thead>
            <tr>
              <th>No.</th>
              <th>OT Name</th>
              <th>OT Rate %</th>
              <th>Working Time</th>
              <th>Hour</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in entries"
              :key="row.id"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td>{{ idx + 1 }}</td>
              <td>{{ row.otName }}</td>
              <td>{{ formatRatePercent(row.otRate) }}</td>
              <td>{{ formatWorkingSpanHms(row.startTime, row.endTime) }}</td>
              <td>{{ formatHourDurationHms(row.hours) }}</td>
              <td class="actions">
                <button
                  type="button"
                  :disabled="!canEditOvertime"
                  @click="editEntry(row)"
                >
                  Edit
                </button>
                <label
                  class="toggle-switch"
                  :class="{ 'toggle-switch--disabled': !canEditOvertime }"
                  title="Active"
                >
                  <input
                    type="checkbox"
                    :checked="row.active"
                    :disabled="!canEditOvertime"
                    @change="toggleActive(row)"
                  >
                  <span class="slider"></span>
                </label>
              </td>
            </tr>
            <tr v-if="entries.length === 0">
              <td colspan="6" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                No overtime entries saved.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref } from 'vue'
import FinanceToast from '../FinanceToast.vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'

const DEFAULT_TIME = '00:00:00'

const props = defineProps({
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Overtime'
  }
})

const store = useStore()

const overtimeApiPath = computed(() => `${props.apiBase}/overtime`)

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddOvertime = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Add')
})

const canEditOvertime = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = ref({
  otName: '',
  otRate: '',
  startTime: DEFAULT_TIME,
  endTime: DEFAULT_TIME
})

const editingId = ref(null)
const entries = ref([])
const isLoadingRows = ref(false)
const isSaving = ref(false)
const toast = ref({ show: false, message: '', variant: 'success' })
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.value = { show: true, message, variant }
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.value = { show: false, message: '', variant: 'success' }
    toastTimer = null
  }, duration)
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function normalizeTimeFieldOnBlur(field) {
  let v = form.value[field]
  v = v === null || v === undefined ? '' : String(v).trim()
  if (!v) {
    form.value[field] = DEFAULT_TIME
    return
  }
  const sec = parseTimeToSeconds(v)
  form.value[field] = sec !== null ? clockSecondsToHms(sec) : DEFAULT_TIME
}

function timePickerValueToHms(raw) {
  if (!raw || typeof raw !== 'string') return null
  const normalized = raw.length === 5 ? `${raw}:00` : raw
  const sec = parseTimeToSeconds(normalized)
  return sec !== null ? clockSecondsToHms(sec) : null
}

function onNativeTimePick(field, evt) {
  const hms = timePickerValueToHms(evt.target.value)
  if (hms) form.value[field] = hms
}

function parseTimeToSeconds(t) {
  if (!t || typeof t !== 'string') return null
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  const sec = m[3] !== undefined ? Number(m[3]) : 0
  if (
    !Number.isFinite(h) ||
    !Number.isFinite(min) ||
    !Number.isFinite(sec) ||
    min < 0 ||
    min > 59 ||
    sec < 0 ||
    sec > 59 ||
    h < 0 ||
    h > 23
  )
    return null
  return h * 3600 + min * 60 + sec
}

function clockSecondsToHms(secondsInDay) {
  let s = ((secondsInDay % 86400) + 86400) % 86400
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`
}

function normalizeClockToHms(t) {
  const sec = parseTimeToSeconds(t)
  return sec !== null ? clockSecondsToHms(sec) : String(t ?? '')
}

function computeHoursBetween(startTime, endTime) {
  const startS = parseTimeToSeconds(startTime)
  const endS = parseTimeToSeconds(endTime)
  if (startS === null || endS === null) return null
  let diff = endS - startS
  if (diff < 0) diff += 24 * 3600
  return diff / 3600
}

function formatHourDurationHms(decimalHours) {
  const n = Number(decimalHours)
  if (!Number.isFinite(n) || n < 0) return '00:00:00'
  const totalSecs = Math.round(n * 3600)
  const hh = Math.floor(totalSecs / 3600)
  const mm = Math.floor((totalSecs % 3600) / 60)
  const ss = totalSecs % 60
  return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`
}

function formatRatePercent(val) {
  if (val === null || val === undefined) return ''
  const s = typeof val === 'string' ? val.trim() : String(val).trim()
  if (!s) return ''
  return `${s}%`
}

function formatWorkingSpanHms(startTime, endTime) {
  if (!startTime || !endTime) return ''
  return `${normalizeClockToHms(startTime)} - ${normalizeClockToHms(endTime)}`
}

function normalizeHoursForRow(startTime, endTime) {
  const h = computeHoursBetween(startTime, endTime)
  return Number.isFinite(h) ? h : 0
}

function resetForm() {
  form.value = { otName: '', otRate: '', startTime: DEFAULT_TIME, endTime: DEFAULT_TIME }
  editingId.value = null
}

async function loadEntries() {
  isLoadingRows.value = true
  try {
    const res = await apiFetch(overtimeApiPath.value)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load overtime.', 'error')
      entries.value = []
      return
    }
    const data = await res.json().catch(() => [])
    entries.value = Array.isArray(data)
      ? data.map((row) => ({
          ...row,
          hours: normalizeHoursForRow(row.startTime, row.endTime)
        }))
      : []
  } catch {
    showToast('Failed to load overtime.', 'error')
    entries.value = []
  } finally {
    isLoadingRows.value = false
  }
}

async function save() {
  if (isSaving.value) return
  if (editingId.value != null) {
    if (!canEditOvertime.value) return
  } else if (!canAddOvertime.value) {
    return
  }

  const otName = String(form.value.otName || '').trim()
  const otRate = String(form.value.otRate ?? '').trim()
  const otRateNum = Number(otRate)
  const startTime = form.value.startTime
  const endTime = form.value.endTime
  if (!otName || !otRate || !Number.isFinite(otRateNum) || otRateNum < 0 || !startTime || !endTime) return

  const hours = computeHoursBetween(startTime, endTime)
  if (hours === null) return

  const body = { otName, otRate, startTime, endTime }

  isSaving.value = true
  try {
    if (editingId.value != null) {
      const res = await apiFetch(`${overtimeApiPath.value}/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        showToast(err.message || 'Failed to update overtime.', 'error')
        return
      }
      showToast('Overtime updated successfully.', 'success')
      resetForm()
      await loadEntries()
      return
    }

    const res = await apiFetch(overtimeApiPath.value, {
      method: 'POST',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save overtime.', 'error')
      return
    }
    showToast('Overtime saved successfully.', 'success')
    resetForm()
    await loadEntries()
  } catch {
    showToast('Failed to save overtime.', 'error')
  } finally {
    isSaving.value = false
  }
}

function editEntry(row) {
  if (!canEditOvertime.value) return
  editingId.value = row.id
  form.value = {
    otName: row.otName,
    otRate: row.otRate != null && row.otRate !== '' ? String(row.otRate) : '',
    startTime: row.startTime,
    endTime: row.endTime
  }
}

async function toggleActive(row) {
  if (!canEditOvertime.value) return
  const nextActive = !row.active
  try {
    const res = await apiFetch(`${overtimeApiPath.value}/${row.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active: nextActive })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to update status.', 'error')
      return
    }
    const idx = entries.value.findIndex((r) => r.id === row.id)
    if (idx >= 0) entries.value[idx] = { ...entries.value[idx], active: nextActive }
  } catch {
    showToast('Failed to update status.', 'error')
  }
}

onMounted(() => {
  loadEntries()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.actions button:disabled {
  color: #94a3b8;
  cursor: not-allowed;
  text-decoration: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>
